import {
  onBeforeMount,
  onBeforeUnmount,
  provide,
  inject,
  ref,
  type InjectionKey,
  type Ref
} from 'vue'
import { emitter } from '@/plugins/emitter'

export type EntrySheetHistoryItem = {
  year: number | ''
  month: number | ''
  description: string
  uuid: string
}

export type EntrySheetFormatData = {
  createDate: Date | null
  writeDate?: string
  educationalHistories: EntrySheetHistoryItem[]
  jobHistories: EntrySheetHistoryItem[]
  licenseHistories: EntrySheetHistoryItem[]
  commuteDescription: string
  commuteHours: string
  commuteMinutes: string
  spouseNumber: string
  hasSpouse: boolean
  underDutyToSupport: boolean
  reason: string
  reasonNew: string
  request: string
  nowNensyu: string
  kibouNensyu: string
  notDisplayRecommend: boolean
  notDisplayIjyo?: boolean
  gradeYear?: string
  studyHistory?: string
  clubActivity?: string
  licenseHistory?: string
  skillList?: string
  parttimeJob?: string
  registYagioffer?: boolean
}

const ENTRY_SHEET_FORMAT_DATA_KEY: InjectionKey<EntrySheetFormatData> = Symbol(
  'entrySheetFormatData'
)

export function provideEntrySheetFormatData(formatData: EntrySheetFormatData) {
  provide(ENTRY_SHEET_FORMAT_DATA_KEY, formatData)
}

export function useEntrySheetFormatData(): EntrySheetFormatData {
  const formatData = inject(ENTRY_SHEET_FORMAT_DATA_KEY)
  if (!formatData) {
    throw new Error(
      'useEntrySheetFormatData() must be used under EntrySheetEdit'
    )
  }
  return formatData
}

type HistoryColumns = {
  year?: number | string
  month?: number | string
  description?: string
}

const DEACTIVATE_HISTORY_ITEM = 'deactivate-history-item'

function uuidv4(): string {
  const cryptoObj =
    window.crypto || (window as Window & { msCrypto?: Crypto }).msCrypto
  const template = '10000000-1000-4000-8000-100000000000'
  return template.replace(/[018]/g, (c) =>
    (
      Number(c) ^
      (cryptoObj!.getRandomValues(new Uint8Array(1))[0] &
        (15 >> (Number(c) / 4)))
    ).toString(16)
  )
}

function createHistoryItem(columns?: HistoryColumns): EntrySheetHistoryItem {
  const cols = columns || {}
  return {
    year: Number(cols.year) || '',
    month: Number(cols.month) || '',
    description: cols.description || '',
    uuid: uuidv4()
  }
}

function addHistory(
  historyList: EntrySheetHistoryItem[],
  index: number | undefined | null,
  columns?: HistoryColumns
) {
  const newHistoryItem = createHistoryItem(columns)
  if (typeof index === 'number') {
    historyList.splice(index, 0, newHistoryItem)
  } else {
    historyList.push(newHistoryItem)
  }
}

function moveHistory(
  historyList: EntrySheetHistoryItem[],
  params: { index: number; to: string }
) {
  const fromIndex = params.index
  const to = params.to
  if (fromIndex === 0 && to === 'up') {
    console.log('first item skip')
    return
  }
  if (fromIndex + 1 === historyList.length && to === 'down') {
    console.log('last item skip')
    return
  }
  if (to === 'up') {
    historyList.splice(
      fromIndex - 1,
      2,
      historyList[fromIndex],
      historyList[fromIndex - 1]
    )
  } else if (to === 'down') {
    historyList.splice(
      fromIndex,
      2,
      historyList[fromIndex + 1],
      historyList[fromIndex]
    )
  } else {
    console.log('param "to" must be "up" or "down"')
  }
}

export function createEmptyEntrySheetFormatData(): EntrySheetFormatData {
  return {
    createDate: null,
    educationalHistories: [],
    jobHistories: [],
    licenseHistories: [],
    commuteDescription: '',
    commuteHours: '',
    commuteMinutes: '',
    spouseNumber: '',
    hasSpouse: false,
    underDutyToSupport: false,
    reason: '',
    reasonNew: '',
    request: '',
    nowNensyu: '',
    kibouNensyu: '',
    notDisplayRecommend: true,
    notDisplayIjyo: false,
    gradeYear: '',
    studyHistory: '',
    clubActivity: '',
    licenseHistory: '',
    skillList: '',
    parttimeJob: '',
    registYagioffer: true
  }
}

export function useEntrySheetHistories(formatData: EntrySheetFormatData): {
  activeHistoryItem: Ref<string>
  educationalResetDialogVisible: Ref<boolean>
  setEducationalBackgrounds: (
    historyList: Record<string, { year: number; month: number; desc: string }>
  ) => void
  resetEducationalBackgrounds: () => void
  addEducationalHistory: (
    index?: number | null,
    columns?: HistoryColumns
  ) => void
  deleteEducationalHistory: (index: number) => void
  moveEducationalHistory: (params: { index: number; to: string }) => void
  activateHistoryItem: (id: string) => void
  deactivateHistoryItem: () => void
} {
  const activeHistoryItem = ref('')
  const educationalResetDialogVisible = ref(false)

  function setEducationalBackgrounds(
    historyList: Record<string, { year: number; month: number; desc: string }>
  ) {
    formatData.educationalHistories = []
    let index = 0
    for (const key in historyList) {
      const item = historyList[key]
      addEducationalHistory(index++, {
        year: item.year,
        month: item.month,
        description: item.desc
      })
    }
  }

  function resetEducationalBackgrounds() {
    formatData.educationalHistories = []
  }

  function addEducationalHistory(
    index?: number | null,
    columns?: HistoryColumns
  ) {
    if (formatData.educationalHistories == null) {
      formatData.educationalHistories = []
    }
    addHistory(formatData.educationalHistories, index, columns)
  }

  function deleteEducationalHistory(index: number) {
    formatData.educationalHistories.splice(index, 1)
  }

  function moveEducationalHistory(params: { index: number; to: string }) {
    moveHistory(formatData.educationalHistories, params)
  }

  function activateHistoryItem(id: string) {
    activeHistoryItem.value = id
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  function deactivateHistoryItem() {
    activeHistoryItem.value = ''
  }

  onBeforeMount(() => {
    emitter.on(DEACTIVATE_HISTORY_ITEM, deactivateHistoryItem)
  })

  onBeforeUnmount(() => {
    emitter.off(DEACTIVATE_HISTORY_ITEM, deactivateHistoryItem)
  })

  return {
    activeHistoryItem,
    educationalResetDialogVisible,
    setEducationalBackgrounds,
    resetEducationalBackgrounds,
    addEducationalHistory,
    deleteEducationalHistory,
    moveEducationalHistory,
    activateHistoryItem,
    deactivateHistoryItem
  }
}
